// Contract domain types - per Schema_Specification Section 3.7 (2.8.x / 2.2.5).

import i18n from '@/i18n'
import type { ListQuery } from './index'
import type { Client } from './clients'
import type { LineItem } from './billing'

// Mirrors App\Const\ContractStatus on the backend (single source of truth for
// the contract lifecycle + signature states).
export type ContractStatus =
  | 'draft'
  | 'pending_signature'
  | 'signed'
  | 'declined'
  | 'active'
  | 'to_be_renewed'
  | 'suspended'
  | 'expired'
  | 'terminated'
  | 'renewed'

// Contract type (feedback #54). Mirrors App\Const\ContractType on the backend.
export type ContractType =
  | 'rodent_control'
  | 'insect_control'
  | 'rodent_insect_control'
  | 'haccp'
  | 'multi_site'

export const CONTRACT_TYPES: ContractType[] = [
  'rodent_control',
  'insect_control',
  'rodent_insect_control',
  'haccp',
  'multi_site',
]

// Intervention planning frequency (feedback #58).
export type PlanningFrequency =
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'biannual'
  | 'annually'
  | 'custom'

export const PLANNING_FREQUENCIES: PlanningFrequency[] = [
  'monthly',
  'bimonthly',
  'quarterly',
  'biannual',
  'annually',
  'custom',
]

export type Weekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export const WEEKDAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

// A preferred time-slot card (feedback #58, mirrors the Jobs 2.3.3 UI).
export interface ContractTimeSlot {
  start: string
  end: string
}

// An "items to bring" row (feedback #57). Persisted as JSON, separate from the
// billable contract_lines so it does not affect the contract amount.
export interface ContractBringItem {
  service_item_id: string | null
  name: string
  quantity: number
  unit?: string | null
  image_url?: string | null
}

export interface Contract {
  id: string
  tenant_id: string
  number: string | null
  title?: string | null
  client_id: string
  quote_id: string | null
  contract_type?: ContractType | null
  start_date: string | null
  end_date: string | null
  status: ContractStatus
  frequency: string | null
  amount: number | null
  vat_rate?: number | null
  signature_request_id: string | null
  signed_at: string | null
  sent_at?: string | null
  // List "Next action" badge (2.8.1): renew | relaunch_signature | null.
  next_action?: 'renew' | 'relaunch_signature' | null
  auto_renew: boolean
  // Whether sending the contract should request a YouSign e-signature. Drives
  // the default state of the "request e-signature" checkbox in the send modal.
  require_signature?: boolean
  created_at: string
  updated_at: string
  // Optional derived fields surfaced by list / contracts-to-renew (2.2.5 / 2.8.1).
  client?: Client | null
  client_name?: string | null
  days_remaining?: number | null
}

export interface ContractDetail extends Contract {
  client: Client
  summary?: ContractDetailSummary
  // Optional line items carried over from a source quote when present. The
  // backend detail resource exposes them as `lines`.
  lines?: LineItem[]
  terms?: string | null
  // Selected service categories (#56) - persisted ids + resolved {id,name} pairs
  // surfaced by the detail resource for display (#61).
  service_category_ids?: string[]
  service_categories?: { id: string; name: string }[]
  // Items to bring (#57).
  items_to_bring?: ContractBringItem[]
  // Intervention planning (#58).
  interventions_per_year?: number | null
  planning_frequency?: PlanningFrequency | null
  preferred_days?: Weekday[]
  preferred_time_slots?: ContractTimeSlot[]
  intervention_resume?: string[]
  notes?: string | null
}

export interface ContractDetailSummary {
  total_amount: number | null
  billing_frequency: string | null
  completed_interventions_count: number
  planned_interventions_count: number | null
  next_intervention: {
    id: string
    scheduled_date: string | null
    start_time: string | null
    end_time: string | null
  } | null
}

// Contract list KPI summary (2.8.1 / Figma 3396:160432). Tenant-wide counts
// plus annual amounts for the Active / Renew tiles.
export interface ContractSummary {
  active: number
  active_amount: number
  pending_signature: number
  pending_signature_this_month: number
  to_renew: number
  to_renew_amount: number
  expired: number
}

export interface ContractHistory {
  id: string
  tenant_id: string
  contract_id: string
  event: string
  snapshot: Record<string, unknown> | null
  causer_id: string | null
  causer_name: string | null
  created_at: string
}

export interface CreateContractRequest {
  client_id: string
  title?: string | null
  quote_id?: string
  contract_type?: ContractType
  start_date?: string
  end_date?: string
  frequency?: string
  amount?: number
  vat_rate?: number
  auto_renew?: boolean
  require_signature?: boolean
  // Service categories covered by the contract (#56).
  service_category_ids: string[]
  // Items to bring (#57).
  items_to_bring?: ContractBringItem[]
  // Intervention planning (#58).
  interventions_per_year: number
  planning_frequency: PlanningFrequency
  preferred_days: Weekday[]
  preferred_time_slots: ContractTimeSlot[]
  intervention_resume?: string[]
  notes?: string
}

export type EditContractRequest = Partial<CreateContractRequest>

export interface RenewContractRequest {
  end_date: string
  amount?: number
}

export interface ContractStatusUpdateRequest {
  status: ContractStatus
  termination_date?: string
  termination_reason?: string
}

export interface SendSignatureRequest {
  recipient_email: string
  // YouSign e-signature when true (link in email); PDF-only send when false (2.8.8).
  require_signature?: boolean
  message?: string
}

export interface ContractListQuery extends ListQuery {
  status?: string | string[]
  client_id?: string
  end_date_from?: string
  end_date_to?: string
  // Convenience filter used by the contracts-to-renew "View all" deep link (2.2.5).
  expiring?: boolean
}

// --- Display label maps (Test_Cases / AC labels for 2.8.x) ---
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: i18n.t('enums:contractStatus.draft'),
  pending_signature: i18n.t('enums:contractStatus.pending_signature'),
  signed: i18n.t('enums:contractStatus.signed'),
  declined: i18n.t('enums:contractStatus.declined'),
  active: i18n.t('enums:contractStatus.active'),
  to_be_renewed: i18n.t('enums:contractStatus.to_be_renewed'),
  suspended: i18n.t('enums:contractStatus.suspended'),
  expired: i18n.t('enums:contractStatus.expired'),
  terminated: i18n.t('enums:contractStatus.terminated'),
  renewed: i18n.t('enums:contractStatus.renewed'),
}

// Contract type labels (feedback #54).
export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  rodent_control: i18n.t('enums:contractType.rodent_control'),
  insect_control: i18n.t('enums:contractType.insect_control'),
  rodent_insect_control: i18n.t('enums:contractType.rodent_insect_control'),
  haccp: i18n.t('enums:contractType.haccp'),
  multi_site: i18n.t('enums:contractType.multi_site'),
}

// Intervention planning frequency labels (feedback #58).
export const PLANNING_FREQUENCY_LABELS: Record<PlanningFrequency, string> = {
  monthly: i18n.t('enums:planningFrequency.monthly'),
  bimonthly: i18n.t('enums:planningFrequency.bimonthly'),
  quarterly: i18n.t('enums:planningFrequency.quarterly'),
  biannual: i18n.t('enums:planningFrequency.biannual'),
  annually: i18n.t('enums:planningFrequency.annually'),
  custom: i18n.t('enums:planningFrequency.custom'),
}

// Weekday labels (feedback #58).
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  monday: i18n.t('enums:weekday.monday'),
  tuesday: i18n.t('enums:weekday.tuesday'),
  wednesday: i18n.t('enums:weekday.wednesday'),
  thursday: i18n.t('enums:weekday.thursday'),
  friday: i18n.t('enums:weekday.friday'),
  saturday: i18n.t('enums:weekday.saturday'),
  sunday: i18n.t('enums:weekday.sunday'),
}

// Recurrence / billing-cycle options (2.8.2 frequency field).
export const CONTRACT_FREQUENCY_LABELS: Record<string, string> = {
  monthly: i18n.t('enums:contractFrequency.monthly'),
  quarterly: i18n.t('enums:contractFrequency.quarterly'),
  biannual: i18n.t('enums:contractFrequency.biannual'),
  yearly: i18n.t('enums:contractFrequency.yearly'),
  one_off: i18n.t('enums:contractFrequency.one_off'),
}
